// src/pages/AdminPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../components/AuthGate";
import AdminPicks from "./AdminPicks";
import AdminAddAcca from "./AdminAddAcca";
import AdminTipsterApplications from "./AdminTipsterApplications";
import AdminModelHealthPanel from "../components/AdminModelHealthPanel";
import AdminSocialMonitoringPanel from "../components/AdminSocialMonitoringPanel";
import AdminRoomQuestionsPanel from "../components/AdminRoomQuestionsPanel";
import AdminDailyPlayerPanel from "../components/AdminDailyPlayerPanel";

const ADMIN_TABS = [
  { id: "featured", icon: "⚡", label: "Featured Picks", description: "Fixture picks and public promotion" },
  { id: "accas", icon: "＋", label: "Acca Builder", description: "Build curated multi-leg selections" },
  { id: "tipsters", icon: "✓", label: "Applications", description: "Review new tipster requests" },
  { id: "model", icon: "◫", label: "Model Health", description: "Calibration and edge performance" },
  { id: "social", icon: "◎", label: "Social", description: "Community and room activity" },
  { id: "daily-player", icon: "?", label: "Daily Player", description: "Queue and publish daily challenges" },
];

const panelStyle = {
  borderRadius: 22,
  border: "1px solid rgba(134,239,172,0.14)",
  background: "linear-gradient(180deg, rgba(10,25,19,0.96), rgba(4,13,10,0.98))",
  padding: "clamp(1rem, 2vw, 1.5rem)",
  boxShadow: "0 24px 60px rgba(0,0,0,0.24)",
};

function AdminSectionShell({ eyebrow, title, description, children }) {
  return (
    <section style={panelStyle}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ color: "#31d48b", fontSize: 11, fontWeight: 900, letterSpacing: 1.7, textTransform: "uppercase" }}>
          {eyebrow}
        </div>
        <h2 style={{ margin: "6px 0 5px", color: "#f0fdf4", fontSize: "clamp(1.25rem, 2vw, 1.65rem)" }}>
          {title}
        </h2>
        <p style={{ maxWidth: 760, margin: 0, color: "#91aa9c", fontSize: "0.88rem", lineHeight: 1.55 }}>
          {description}
        </p>
      </div>
      {children}
    </section>
  );
}

export default function AdminPage() {
  const nav = useNavigate();
  const { firebaseUser, profile, isAdmin, loading, logout } = useAuth();

  const [activeTab, setActiveTab] = useState("featured"); // "featured" | "accas" | "tipsters" | "model" | "social"

  // Gate access: only admins allowed
  useEffect(() => {
    if (loading) return;

    if (!firebaseUser || !profile) {
      nav("/login");
      return;
    }

    if (!isAdmin) {
      nav("/");
    }
  }, [loading, firebaseUser, profile, isAdmin, nav]);

  if (loading || !firebaseUser || !profile || !isAdmin) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#020814",
          color: "#e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.95rem",
        }}
      >
        Checking admin access…
      </div>
    );
  }

  const email = profile?.email || firebaseUser.email;

  const tabCardStyle = (tab) => ({
    minHeight: 94,
    padding: "14px 15px",
    borderRadius: 16,
    textAlign: "left",
    display: "flex",
    gap: 11,
    alignItems: "flex-start",
    minWidth: 0,
    fontSize: "0.8rem",
    cursor: "pointer",
    border:
      activeTab === tab
        ? "1px solid rgba(49,212,139,0.72)"
        : "1px solid rgba(148,163,184,0.16)",
    background:
      activeTab === tab
        ? "linear-gradient(145deg, rgba(17,74,48,.72), rgba(8,34,24,.92))"
        : "rgba(255,255,255,0.025)",
    color: activeTab === tab ? "#f9fafb" : "#e5e7eb",
    boxShadow: activeTab === tab ? "0 12px 30px rgba(22,163,74,.14)" : "none",
  });

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at 50% -10%, rgba(22,101,52,.35), transparent 42%), linear-gradient(180deg, #030b08 0%, #020705 100%)",
        color: "#e5e7eb",
        padding: "clamp(1rem, 2.5vw, 2rem)",
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        {/* Top bar */}
        <header
          style={{
            position: "relative",
            overflow: "hidden",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 20,
            marginBottom: "1.25rem",
            flexWrap: "wrap",
            padding: "clamp(1.25rem, 3vw, 2rem)",
            borderRadius: 24,
            border: "1px solid rgba(134,239,172,.17)",
            background: "linear-gradient(135deg, rgba(8,31,21,.98), rgba(3,15,10,.98))",
            boxShadow: "0 24px 70px rgba(0,0,0,.28)",
          }}
        >
          <div style={{ position: "absolute", width: 360, height: 360, borderRadius: "50%", right: -120, top: -270, background: "rgba(49,212,139,.18)", filter: "blur(28px)", pointerEvents: "none" }} />
          <div style={{ position: "relative" }}>
            <div style={{ color: "#31d48b", fontSize: 11, fontWeight: 900, letterSpacing: 2, textTransform: "uppercase" }}>
              CSB Operations
            </div>
            <h1
              style={{
                margin: "7px 0 0",
                color: "#f0fdf4",
                fontSize: "clamp(2rem, 4vw, 3.1rem)",
                letterSpacing: "-0.045em",
                lineHeight: 1,
              }}
            >
              Control Centre
            </h1>
            <p
              style={{
                margin: "0.65rem 0 0",
                fontSize: "0.9rem",
                color: "#91aa9c",
              }}
            >
              Picks, models, tipsters and community operations in one workspace.
            </p>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 14, color: "#b9cec1", fontSize: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#31d48b", boxShadow: "0 0 16px rgba(49,212,139,.8)" }} />
              Admin session · <strong style={{ color: "#e7f5ec" }}>{email}</strong>
            </div>
          </div>

          <div style={{ position: "relative", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Link to="/admin/moderation" style={{fontSize:"0.8rem",fontWeight:800,padding:"0.6rem 0.85rem",borderRadius:11,border:"1px solid rgba(49,212,139,0.42)",background:"rgba(49,212,139,.08)",textDecoration:"none",color:"#86efac"}}>Moderation queue</Link>
            <Link
              to="/"
              style={{
                fontSize: "0.8rem",
                fontWeight: 800,
                padding: "0.6rem 0.85rem",
                borderRadius: 11,
                border: "1px solid rgba(148,163,184,0.22)",
                background: "rgba(255,255,255,.025)",
                textDecoration: "none",
                color: "#e5e7eb",
              }}
            >
              View site ↗
            </Link>
            <button
              onClick={logout}
              style={{
                fontSize: "0.8rem",
                fontWeight: 800,
                padding: "0.6rem 0.85rem",
                borderRadius: 11,
                border: "1px solid rgba(248,113,113,0.3)",
                background: "rgba(248,113,113,.05)",
                color: "#fecaca",
                cursor: "pointer",
              }}
            >
              Log out
            </button>
          </div>
        </header>

        {/* Workspace navigation */}
        <section style={{ marginBottom: "1.25rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
            {ADMIN_TABS.map((tab) => (
              <button key={tab.id} type="button" style={tabCardStyle(tab.id)} onClick={() => setActiveTab(tab.id)}>
                <span style={{ width: 31, height: 31, borderRadius: 10, display: "grid", placeItems: "center", flexShrink: 0, background: activeTab === tab.id ? "rgba(49,212,139,.18)" : "rgba(255,255,255,.045)", color: activeTab === tab.id ? "#86efac" : "#91aa9c", fontSize: 17, fontWeight: 900 }}>
                  {tab.icon}
                </span>
                <span style={{ minWidth: 0 }}>
                  <strong style={{ display: "block", color: activeTab === tab.id ? "#f0fdf4" : "#dce9df", fontSize: 13 }}>{tab.label}</strong>
                  <span style={{ display: "block", marginTop: 5, color: "#789081", fontSize: 11, lineHeight: 1.35 }}>{tab.description}</span>
                </span>
              </button>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 9 }}>
            <Link to="/admin/tipsters" style={{ color: "#86efac", textDecoration: "none", fontSize: 12, fontWeight: 800 }}>
              Open tipster management →
            </Link>
          </div>
        </section>

        <Link to="/admin/football-research" style={{color:"#9ae6b4",display:"inline-block",marginBottom:20}}>Open football intelligence research →</Link>
        {/* Active tab content */}
        {activeTab === "featured" && (
          <AdminSectionShell
            eyebrow="Daily operations"
            title="Featured Picks & Fixtures"
            description="Attach featured selections to fixtures for the public dashboard and CSB marketing channels."
          >
            <div
              style={{
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(148,163,184,.12)",
                background: "#020a07",
              }}
            >
              <AdminPicks />
            </div>
          </AdminSectionShell>
        )}

        {activeTab === "accas" && (
          <AdminSectionShell
            eyebrow="Curation"
            title="Acca Builder"
            description="Combine match markets and player props into curated CSB accumulators."
          >
            <div
              style={{
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(148,163,184,.12)",
                background: "#020a07",
              }}
            >
              <AdminAddAcca />
            </div>
          </AdminSectionShell>
        )}

        {activeTab === "tipsters" && (
          <AdminSectionShell
            eyebrow="Creator network"
            title="Tipster Applications"
            description="Review applicants and approve trusted creators for verified CSB tipster profiles."
          >
            <AdminTipsterApplications />
          </AdminSectionShell>
        )}

        {activeTab === "model" && (
          <AdminSectionShell
            eyebrow="Model operations"
            title="Model Health"
            description="Review edge performance and calibration, then run controlled recompute actions."
          >
            <AdminModelHealthPanel />
          </AdminSectionShell>
        )}

        {activeTab === "social" && (
          <AdminSectionShell
            eyebrow="Community operations"
            title="Social Monitoring"
            description="Track comments, polls, MOTM voting, fixture engagement and fan-room activity."
          >
            <AdminSocialMonitoringPanel />

            <div style={{ marginTop: 16 }}>
              <AdminRoomQuestionsPanel />
            </div>
          </AdminSectionShell>
        )}
        {activeTab === "daily-player" && (
          <AdminSectionShell eyebrow="Daily engagement" title="Guess the Player" description="Choose today’s footballer or queue future challenges for an automatic 10:00 UK release and device notification.">
            <AdminDailyPlayerPanel />
          </AdminSectionShell>
        )}
      </div>
    </div>
  );
}
